import { api, getApiBaseUrl, getAuthToken } from './apiClient';

export type RecordingResponse = {
  id: string;
  patientId?: string | null;
  patientName: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  durationSeconds?: number | null;
  duration: string;
  status: string;
  type: 'audio';
  date: string;
  expiresAt: string;
  url?: string;
  transcriptionId?: string;
  transcription?: string;
};

export type PresignRecordingUploadPayload = {
  patientId?: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  durationSeconds?: number;
};

export type PresignRecordingUploadResponse = {
  recording: RecordingResponse;
  uploadUrl: string;
  expiresInSeconds: number;
};

export function fetchRecordings(): Promise<RecordingResponse[]> {
  return api.get<RecordingResponse[]>('/recordings');
}

export function presignRecordingUpload(payload: PresignRecordingUploadPayload): Promise<PresignRecordingUploadResponse> {
  return api.post<PresignRecordingUploadResponse>('/recordings/presign-upload', payload);
}

export function completeRecordingUpload(recordingId: string): Promise<RecordingResponse> {
  return api.post<RecordingResponse>(`/recordings/${recordingId}/complete`, {});
}

export async function uploadRecordingViaApi(payload: {
  file: File | Blob;
  fileName: string;
  contentType: string;
  patientId?: string;
  durationSeconds?: number;
}): Promise<RecordingResponse> {
  const token = getAuthToken();
  if (!token) throw new Error('Sessão expirada. Faça login novamente.');

  const file =
    payload.file instanceof File
      ? payload.file
      : new File([payload.file], payload.fileName || 'audio.webm', {
          type: payload.contentType || 'audio/webm',
        });
  const form = new FormData();
  if (payload.patientId) form.append('patientId', payload.patientId);
  if (payload.durationSeconds) form.append('durationSeconds', String(payload.durationSeconds));
  form.append('audio', file, payload.fileName || file.name || 'audio.webm');

  const res = await fetch(`${getApiBaseUrl()}/recordings/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  const text = await res.text();
  if (!res.ok) {
    let message = `Erro ${res.status}`;
    try {
      const json = JSON.parse(text) as { message?: string; error?: string };
      message = json.message || json.error || message;
    } catch {
      if (text) message = text.slice(0, 220);
    }
    throw new Error(message);
  }
  return JSON.parse(text) as RecordingResponse;
}

export function getRecordingPlaybackUrl(recordingId: string): Promise<{ url: string; expiresInSeconds: number }> {
  return api.get<{ url: string; expiresInSeconds: number }>(`/recordings/${recordingId}/playback-url`);
}

export function updateRecordingPatient(recordingId: string, patientId?: string): Promise<RecordingResponse> {
  return api.patch<RecordingResponse>(`/recordings/${recordingId}`, { patientId: patientId || null });
}

export function deleteRecording(recordingId: string): Promise<{ success: true }> {
  return api.delete<{ success: true }>(`/recordings/${recordingId}`);
}

export function deleteRecordingTranscription(recordingId: string): Promise<{ success: true }> {
  return api.delete<{ success: true }>(`/recordings/${recordingId}/transcription`);
}

export async function uploadRecordingFile(payload: {
  file: File | Blob;
  fileName: string;
  contentType: string;
  patientId?: string;
  durationSeconds?: number;
}): Promise<RecordingResponse> {
  return uploadRecordingViaApi(payload);
}

export async function uploadRecordingFileDirectToS3(payload: {
  file: File | Blob;
  fileName: string;
  contentType: string;
  patientId?: string;
  durationSeconds?: number;
}): Promise<RecordingResponse> {
  const presigned = await presignRecordingUpload({
    patientId: payload.patientId,
    fileName: payload.fileName,
    contentType: payload.contentType,
    sizeBytes: payload.file.size,
    durationSeconds: payload.durationSeconds,
  });

  const upload = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': payload.contentType },
    body: payload.file,
  });

  if (!upload.ok) {
    return uploadRecordingViaApi(payload);
  }

  return completeRecordingUpload(presigned.recording.id);
}
